<template>
  <v-card
    flat
    class="table-container elevation-2 beam-table-container border-0"
    :class="{
      'mb-0 rounded-b-0 rounded-t-lg': hasRelationalDiagram,
      'mb-4 rounded-lg': !hasRelationalDiagram
    }"
  >
    <v-toolbar flat class="schema-toolbar" height="56">
      <v-col cols="8" style="display: flex;" class="d-flex align-center subheading pb-2">
        <v-icon
          v-if="showEntityDragHandle"
          class="entity-sort-handle mr-3"
          tabindex="-1"
        >
          mdi-drag-vertical
        </v-icon>
        <quick-rename-input
          :id="`schema-name-${schemaIndex}`"
          :value="schema.name"
          :disabled="store.state.isWorkflowDisabled"
          @onChange="blurSchemaName($event)"
          :max-length="30"
        />
        <v-tooltip v-if="usageStatus === 'unused'" location="bottom" open-delay="300">
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="unused-entity-badge unused-entity-badge--orange ml-3">
              <v-icon size="14" class="mr-1">mdi-alert-circle-outline</v-icon>
              <span>Unused</span>
            </div>
          </template>
          <span>Not used in any Aggregate Root or Read Model</span>
        </v-tooltip>
        <v-tooltip v-else-if="usageStatus === 'only-referenced'" location="bottom" open-delay="300">
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="unused-entity-badge unused-entity-badge--yellow ml-3">
              <v-icon size="14" class="mr-1">mdi-alert-circle-outline</v-icon>
              <span>Unused</span>
            </div>
          </template>
          <span>Only referenced by other unused entities</span>
        </v-tooltip>
        <v-tooltip v-if="schema.type === SchemaType.ENTITY" location="top">
          <template v-slot:activator="{ props }">
            <div
              v-bind="props"
              class="entity-type-chip ml-3"
              :class="hasIdField ? 'entity-type-chip--entity' : 'entity-type-chip--value-object'"
            >
              {{ hasIdField ? 'Entity' : 'Value Object' }}
            </div>
          </template>
          <span>{{ hasIdField ? 'Has an ID field - uniquely identifiable' : 'No ID field - identified by its attributes' }}</span>
        </v-tooltip>
      </v-col>
      <v-spacer></v-spacer>
      <v-col v-if="!store.state.isWorkflowDisabled" xs4 class="text-right pb-2 d-flex align-center justify-end ga-2">
        <workflow-aggregate-select-bounded-context
          v-if="schema.type === SchemaType.ENTITY"
          :entity="schema"
        />
        <v-menu>
          <template #activator="{ props }">
            <v-btn icon="mdi-menu" variant="text" v-bind="props" tabindex="-1" data-test="schema-actions-menu" />
          </template>
          <v-list>
            <v-list-item
              v-if="schema.type === SchemaType.ENTITY"
              @click="focusOnField(`schema-name-${schemaIndex}`)"
            >
              <v-list-item-action>
                <v-icon class="mr-2" color="grey">mdi-pencil</v-icon> Edit Name
              </v-list-item-action>
            </v-list-item>
            <v-list-item v-if="!hideDeleteAction" @click="emit('delete-schema', schema.id)">
              <v-list-item-action>
                <v-icon class="mr-2" color="grey">mdi-delete</v-icon> Delete
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-toolbar>
    <template v-if="schema.type === SchemaType.ENTITY">
      <div
        class="entity-description"
        :class="{
          'entity-description--empty': !schema.description,
          'entity-description--invalid': descriptionInvalid
        }"
        :contenteditable="!store.state.isWorkflowDisabled"
        :data-placeholder="store.state.isWorkflowDisabled ? 'No description' : 'Add a short description…'"
        spellcheck="false"
        @input="checkDescriptionLength"
        @blur="onBlurEntityDescription"
        @keydown.enter.prevent="$event.target.blur()"
        @keydown.esc="restoreEntityDescription($event)"
      >{{ schema.description || '' }}</div>
      <div v-if="descriptionInvalid" class="entity-description-error">
        Description is too long ({{ descriptionLength }}/{{ MAX_DESCRIPTION_LENGTH }} characters). Changes won't be saved until shortened.
      </div>
    </template>
    <v-table class="rounded-0 elevation-1">
      <template v-slot:default>
        <tbody v-if="schema.fields.length > 0">
          <template v-for="(row, index) in rows">
            <!-- type: header -->
            <tr v-if="row.type === 'header'" :key="index" ref="sortableList">
              <td class="field-label">{{ schema.fields.length > 0 ? row['rowLabel'] + ':' : 'Click the plus sign to add a field' }}</td>
              <td
                disabled="true"
                v-for="(field, fieldIndex) in schema.fields"
                :key="refreshKey * schema.fields.length + fieldIndex"
                class="header-cell px-0 field-name-wrapper sortableRow"
              >
                <div class="d-flex align-center h-100">
                  <beam-table-cell
                    :key="idTable(schemaIndex, index, fieldIndex)"
                    :ref="(el) => setCellRef(idTable(schemaIndex, index, fieldIndex), el)"
                    :tab="idTable(schemaIndex, index, fieldIndex)"
                    :is-read-model="schema.type === SchemaType.QUERY"
                    :color="entityCardColor"
                    :class="{'beam-required-field': !!field.tags?.includes('REQUIRED')}"
                    v-model:value="field[row.fieldName]"
                    @leave-cell="leaveCell($event, schemaIndex, index, fieldIndex)"
                    @blur-table-cell="emit('blur-table-cell', $event, schema.id, fieldIndex, row.fieldName, null, event?.id)"
                    tag="div"
                    header
                  />
                </div>
                <v-icon class="sortHandle" v-if='!store.state.isWorkflowDisabled'>mdi-drag-horizontal</v-icon>
                <action-menu
                  v-if="!store.state.isWorkflowDisabled"
                  class="header-action"
                  :style="{ backgroundColor: entityCardColor || '' }"
                  :menu-value="field"
                  :menu-items="[
                    {
                      id: 6,
                      icon: 'mdi-swap-horizontal',
                      text: 'Change data type',
                      children: [
                        { id: 31, text: 'String', icon: 'mdi-format-letter-case', action: () => handleChangeDataType(schema.id, fieldIndex, 'string') },
                        { id: 32, text: 'Number', icon: 'mdi-numeric', action: () => handleChangeDataType(schema.id, fieldIndex, 'number') },
                        { id: 33, text: 'Boolean', icon: 'mdi-toggle-switch', action: () => handleChangeDataType(schema.id, fieldIndex, 'boolean') },
                        { id: 35, text: 'Object', icon: 'mdi-code-json', action: (entityRef) => handleChangeDataType(schema.id, fieldIndex, 'object', entityRef) },
                      ]
                    },
                    {
                      id: 5,
                      icon: 'mdi-asterisk',
                      text: field.tags && field.tags.includes('REQUIRED') ? 'Set as optional' : 'Set as required',
                      action: () => setFieldMandatory(schema.id, fieldIndex, !(field.tags && field.tags.includes('REQUIRED')))
                    },
                    {
                      id: 3,
                      icon: 'mds:temp_preferences_custom',
                      text: field.exampleData?.some((i) => !!i) ? 'Regenerate example data' : 'Generate example data',
                      action: () => generateExampleData(schema.id, fieldIndex)
                    },
                    {
                      id: 2,
                      icon: 'mdi-delete',
                      text: 'Delete',
                      action: () => emit('delete-field', schema.id, fieldIndex)
                    }
                  ]"
                />
                <div class="header-action" v-else>
                  <v-tooltip v-if="isMissingEntity(field[row.fieldName])" location="right">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" style="cursor: default;">⚠️</span>
                    </template>
                    <span>This field on the command has no corresponding <br /> entity field. Ok for transient arguments</span>
                  </v-tooltip>
                  <v-icon
                    v-if="!store.state.isWorkflowDisabled"
                    size="small"
                    @click="emit('delete-field', schema.id, fieldIndex)"
                  >
                    mdi-delete
                  </v-icon>
                </div>
              </td>
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                  <td
                    v-bind="props"
                    class="add-field-button px-0 header-cell text-center captures-description defers-schema-blur"
                    :class="{ 'bg-green-lighten-4': schema.type === SchemaType.QUERY }"
                    :style="{ backgroundColor: entityCardColor || '' }"
                    tabindex="0"
                    @click="!store.state.isWorkflowDisabled ? emit('add-column', schema.id) : null">
                    <v-icon>mds:add</v-icon>
                  </td>
                </template>
                <span>Add column</span>
              </v-tooltip>
            </tr>

            <!-- type: text -->
            <tr v-if="row.type === 'text'" :key="index" class="description-row">
              <td class="field-label">{{ row['rowLabel'] }}:</td>
              <beam-table-cell
                v-for="(field, fieldIndex) in schema.fields" :key="idTable(schemaIndex, index, fieldIndex)"
                :ref="(el) => setCellRef(idTable(schemaIndex, index, fieldIndex), el)"
                :tab="idTable(schemaIndex, index, fieldIndex)"
                v-model:value="field[row.fieldName]"
                :color="subtleEntityCardColor"
                @leave-cell="leaveCell($event, schemaIndex, index, fieldIndex)"
                @blur-table-cell="emit('blur-table-cell', $event, schema.id, fieldIndex, row.fieldName, null, event?.id)"
                cell
              />
              <td :style="{ backgroundColor: subtleEntityCardColor }"></td>
            </tr>

            <!-- type: relation -->
            <tr v-if="row.type === 'relation'" :key="index">
              <td class="field-label">
                {{ row.rowLabel || 'Field Data Type' }}:
              </td>
              <template v-for="(field, fieldIndex) in schema.fields" :key="idTable(schemaIndex, index, fieldIndex)">
                <field-select-entity
                  :ref="(el) => setCellRef(idTable(schemaIndex, index, fieldIndex), el)"
                  :id="idTable(schemaIndex, index, fieldIndex)"
                  :entityId="field[row.fieldName]"
                  :field="field"
                  :fieldIndex="fieldIndex"
                  :schemaId="schema.id"
                  :isEntity="schema.type === SchemaType.ENTITY"
                  :backgroundColor="subtleEntityCardColor"
                  @leave-cell="leaveCell($event, schemaIndex, index, fieldIndex)"
                />
              </template>
              <td :style="{ backgroundColor: subtleEntityCardColor }"></td>
            </tr>

            <!-- type: example-data -->
            <template v-if="row.type === 'example-data'">
              <tr
                v-for="exampleIndex in (getNumberOfExampleDataRows(schema.fields) + 3)"
                :key="index + (exampleIndex - 1)"
                :class="{ 'example-data-row': (exampleIndex - 1) <= getNumberOfExampleDataRows(schema.fields) }"
              >
                <td class="field-label">
                  <action-menu
                    v-if="isRowDeletable(exampleIndex - 1)"
                    class="row-action-menu"
                    menu-icon="mdi-dots-vertical"
                    :menu-items="getExampleRowMenuItems(exampleIndex - 1)"
                  />
                  <template v-if="exampleIndex === 1">
                    Example 1:
                    <v-tooltip location="bottom">
                      <template v-slot:activator="{ props }">
                        <v-icon size="x-small" v-bind="props" class="ml-0">mdi-help-circle-outline</v-icon>
                      </template>
                      <span>Ask for the most common example of data for each field.</span>
                    </v-tooltip>
                  </template>
                  <template v-if="exampleIndex === 2">
                    Example 2:
                    <v-tooltip location="bottom">
                      <template v-slot:activator="{ props }">
                        <v-icon size="x-small" v-bind="props" class="ml-0">mdi-help-circle-outline</v-icon>
                      </template>
                      <span>Ask for the second most common example that is different from the typical row above.</span>
                    </v-tooltip>
                  </template>
                  <template v-if="exampleIndex === 3">
                    Example 3:
                    <v-tooltip location="bottom">
                      <template v-slot:activator="{ props }">
                        <v-icon size="x-small" v-bind="props" class="ml-0">mdi-help-circle-outline</v-icon>
                      </template>
                      <span>Ask for the third most common example that is different from both rows above.</span>
                    </v-tooltip>
                  </template>
                </td>
                <template
                  v-for="(field, fieldIndex) in schema.fields"
                  :key="idTable(schemaIndex, index + exampleIndex - 1, fieldIndex)"
                >
                  <beam-table-cell
                    :ref="(el) => setCellRef(idTable(schemaIndex, index + exampleIndex - 1, fieldIndex), el)"
                    :tab="idTable(schemaIndex, index + exampleIndex - 1, fieldIndex)"
                    v-model:value="field.exampleData[exampleIndex - 1]"
                    @leave-cell="leaveCell($event, schemaIndex, index + exampleIndex - 1, fieldIndex)"
                    @blur-table-cell="emit('blur-table-cell', $event, schema.id, fieldIndex, 'exampleData', exampleIndex - 1, event?.id)"
                    :list="!!field.relatedEntityId && !!field.exampleData[exampleIndex - 1] && isOneToMany(field)"
                    cell
                  />
                </template>
                <td></td>
              </tr>
            </template>
          </template>
        </tbody>
        <tbody v-else>
          <tr>
            <td class="btc-cell">Click the plus sign on the right to add a field!</td>
            <v-tooltip location="bottom">
              <template v-slot:activator="{ props }">
                <td
                  v-bind="props"
                  class="add-field-button px-0 header-cell text-center"
                  :style="{ backgroundColor: entityCardColor || '' }"
                  @click="!store.state.isWorkflowDisabled ? emit('add-column', schema.id) : null">
                  <v-icon>mds:add</v-icon>
                </td>
              </template>
              <span>Add a column</span>
            </v-tooltip>
          </tr>
        </tbody>
      </template>
    </v-table>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import ActionMenu from '@/components/ActionMenu.vue'
import BeamTableCell from '@/components/BeamTableCell'
import QuickRenameInput from '@/components/QuickRenameInput.vue'
import FieldSelectEntity from '@/components/FieldSelectEntity.vue'
import WorkflowAggregateSelectBoundedContext from '@/components/WorkflowAggregateSelectBoundedContext.vue'
import { diagramHelpers } from '@/mixins/diagramHelpers.js'
import workflowApi from '@/api/workflowApi'
import cardTypesApi from '@/api/cardTypesApi'
import Sortable from 'sortablejs'
import safeStructuredClone from '@/utils/clone'
import { isSingularPluralMatch, fieldNameImpliesEntity } from '@/utils/fieldNameMatch'
import { DomainModelRole } from '@/constants/cardTypes'
import { SchemaType, MAX_DESCRIPTION_LENGTH } from '@/constants/schema'
import { isDarkTheme } from '@/composables/useAppTheme'
import useEntityActions from '@/composables/useEntityActions'

const props = defineProps({
  schema: Object,
  schemaIndex: Number,
  rows: Array,
  event: Object,
  hasRelationalDiagram: Boolean,
  hideDeleteAction: Boolean,
  cardIndex: Number,
  showEntityDragHandle: Boolean,
  usageStatus: String
})

const emit = defineEmits([
  'blur-table-cell',
  'blur-schema-name',
  'delete-schema',
  'delete-field',
  'add-column',
  'move-left',
  'move-right',
  'generate-example-data',
  'delete-example-row'
])

const store = useStore()
const { setDataType, setFieldMandatory, setFieldCardinality } = useEntityActions()

const sortableList = ref(null)
const cellRefs = {}
const refreshKey = ref(0)

const workflow = computed(() => store.state.workflow.workflow)
const cardTypes = computed(() => store.state.cardTypes.cardTypes)

const hasIdField = computed(() => (props.schema.fields || []).some((f) => f.name === 'id'))

const entityCardType = computed(() => {
  return diagramHelpers.methods.getAggregateCardType(cardTypes.value)
})

const entityCardColor = computed(() => {
  const alpha = isDarkTheme() ? 0.3 : 0.75
  const baseColor = cardTypesApi.getBackgroundColour(entityCardType.value)
  if (baseColor.startsWith('rgb(')) {
    return baseColor.replace(/^rgb\((.*)\)$/, `rgba($1, ${alpha})`)
  }
  if (baseColor.startsWith('hsl(')) {
    return baseColor.replace(/^hsl\((.*)\)$/, `hsla($1, ${alpha})`)
  }
  return baseColor
})

const subtleEntityCardColor = computed(() => {
  const baseColor = entityCardColor.value || '#FCFCFC'

  if (baseColor.startsWith('hsla(')) {
    return baseColor.replace(/^hsla\((.*),\s*[\d.]+\)$/, 'hsla($1, 0.1)')
  }
  if (baseColor.startsWith('hsl(')) {
    return baseColor.replace(/^hsl\((.*)\)$/, 'hsla($1, 0.1)')
  }

  let rgbValues = ''
  if (baseColor.startsWith('rgba(')) {
    rgbValues = baseColor.replace(/^rgba\((.*),\s*[\d.]+\)$/, '$1')
  } else if (baseColor.startsWith('rgb(')) {
    rgbValues = baseColor.replace(/^rgb\((.*)\)$/, '$1')
  } else if (baseColor.startsWith('#')) {
    const hex = baseColor.replace('#', '')
    const fullHex = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex
    const r = parseInt(fullHex.substring(0, 2), 16)
    const g = parseInt(fullHex.substring(2, 4), 16)
    const b = parseInt(fullHex.substring(4, 6), 16)
    rgbValues = `${r}, ${g}, ${b}`
  } else {
    return '#FAFAFA'
  }

  return `rgba(${rgbValues}, 0.1)`
})

const setCellRef = (key, el) => {
  if (el) {
    cellRefs[key] = el
  } else {
    delete cellRefs[key]
  }
}

const blurSchemaName = (event) => {
  const newName = (event.target.innerText || '').trim()

  if (newName.length === 0) {
    diagramHelpers.methods.restoreValue(event, props.schema.name)

    return
  }

  if (newName.length > 30) {
    alert('Entity name must be max 30 characters')
    diagramHelpers.methods.restoreValue(event, props.schema.name)

    return
  }

  if (props.schema.type === SchemaType.ENTITY && props.schema.boundedContext) {
    const isDuplicate = (workflow.value.schemas || []).some((s) =>
      s.id !== props.schema.id &&
      s.type === SchemaType.ENTITY &&
      s.boundedContext === props.schema.boundedContext &&
      s.name.toLowerCase() === newName.toLowerCase()
    )

    if (isDuplicate) {
      diagramHelpers.methods.restoreValue(event, props.schema.name)
      const bcName = (workflow.value.boundedContexts || []).find((bc) => bc.id === props.schema.boundedContext)?.name || 'this bounded context'
      store.commit('snackbar/showMessage', {
        content: `An entity with the name "${newName}" already exists in ${bcName}`,
        timeout: 4000,
        color: 'red'
      }, { root: true })
      return
    }
  }

  emit('blur-schema-name', event)
}

const isOneToMany = (field) => {
  if (!props.schema.id || !field.relatedEntityId) {
    return false
  }

  const matchingField = props.schema.fields?.find((f) => f.relatedEntityId === field.relatedEntityId)

  return matchingField?.cardinality === 'one-to-many'
}

const focusOnField = (id) => {
  document.getElementById(id).focus()
}

const focusOnRef = (refKey) => {
  const cellRef = cellRefs[refKey]
  if (cellRef) {
    if (cellRef.setFocus) {
      cellRef.setFocus()
    } else {
      document.getElementById(refKey)?.focus()
    }
  }
}

const getNumberOfExampleDataRows = (fields) => {
  let numberOfExampleDataRows = 0
  const allExampleDataArrays = fields.map((field) => field.exampleData)
  for (const exampleDataArray of allExampleDataArrays) {
    if (!Array.isArray(exampleDataArray)) continue
    for (const [index, exampleData] of exampleDataArray.entries()) {
      if (exampleData && exampleData.trim().length > 0) {
        if (index > numberOfExampleDataRows) {
          numberOfExampleDataRows = index
        }
      }
    }
  }

  return numberOfExampleDataRows
}

const idTable = (schemaIndex, row, column) => {
  return (schemaIndex + 1) * 10000 + row * 100 + column
}

const leaveCell = (event, schemaIndex, row, fieldIndex) => {
  const refKey = idTable(schemaIndex, row, fieldIndex)
  switch (event.keyCode) {
    case 13: // enter
      focusOnRef(refKey + 100)
      break
    case 37: // left arrow
      focusOnRef(refKey - 1)
      break
    case 38: // up arrow
      focusOnRef(refKey - 100)
      break
    case 39: // right arrow
      focusOnRef(refKey + 1)
      break
    case 40: // down arrow
      focusOnRef(refKey + 100)
      break
  }
  event.preventDefault()
  event.stopPropagation()
}

const generateExampleData = (schemaId, fieldIndex) => {
  emit('generate-example-data', schemaId, fieldIndex)
}

const deleteExampleDataRow = (rowIndex) => {
  emit('delete-example-row', props.schema.id, rowIndex)
}

const getExampleRowMenuItems = (rowIndex) => {
  if (!store.state.isWorkflowDisabled && rowIndex <= getNumberOfExampleDataRows(props.schema.fields)) {
    return [
      {
        id: 1,
        icon: 'mdi-delete',
        text: 'Delete row',
        action: () => deleteExampleDataRow(rowIndex)
      }
    ]
  }

  return []
}

const isRowDeletable = (rowIndex) => {
  return !store.state.isWorkflowDisabled && rowIndex <= getNumberOfExampleDataRows(props.schema.fields)
}

const initSortable = () => {
  if (!sortableList.value) {
    return
  }

  const el = Array.isArray(sortableList.value) ? sortableList.value[0]?.$el || sortableList.value[0] : sortableList.value?.$el || sortableList.value
  if (!el) {
    return
  }

  // eslint-disable-next-line no-new
  new Sortable(el, {
    draggable: '.sortableRow',
    handle: '.sortHandle',
    ghostClass: 'sortable-ghost',
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    animation: 150,
    scroll: true,
    forceFallback: false,
    scrollSensitivity: 100,
    onEnd: onEndDrag,
    onStart: () => {
      Array.prototype.forEach.call(
        document.querySelectorAll('.v-row-group__header'),
        (el, index) => {
          if (!el.classList.contains('sortableRow') && index !== 0) {
            el.classList.add('sortableRow')
          }
        }
      )
      Array.prototype.forEach.call(
        document.querySelectorAll('.sortableRow:not(.sortable-chosen)'),
        (el, index) => {
          if (!el.classList.contains('white-background-on-hover') && index !== 0) {
            el.classList.add('white-background-on-hover')
          }
        }
      )
    }
  })
}

const onEndDrag = async (dropEvent) => {
  const fromIndex = dropEvent.oldIndex - 1
  const toIndex = dropEvent.newIndex - 1

  const schemaIndex = workflow.value.schemas.findIndex((model) => model.id === props.schema.id)
  if (schemaIndex === -1) {
    return
  }

  refreshKey.value++

  const newSchemas = safeStructuredClone(workflow.value.schemas || [])
  const field = newSchemas[schemaIndex].fields[fromIndex]
  newSchemas[schemaIndex].fields.splice(fromIndex, 1)
  newSchemas[schemaIndex].fields.splice(toIndex, 0, field)

  await workflowApi.updateSchemas({
    schemas: newSchemas,
    workflow: workflow.value
  })
}

const isMissingEntity = (commandField) => {
  if (props.schema.type === SchemaType.ENTITY) {
    return false
  }

  const card = props.event?.cards?.find((c) => c.schemaId === props.schema.id)
  if (!card) {
    return false
  }

  let rootEntityId
  if (props.schema.type === SchemaType.QUERY) {
    rootEntityId = props.schema.entityId
  } else {
    const entityCard = props.event?.cards?.find((c) => c.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT)
    rootEntityId = entityCard?.schemaId
  }
  const workflowSchemas = workflow.value?.schemas || []

  if (!workflowSchemas.length) {
    return true
  }

  const relevantEntities = workflowSchemas
    .filter((schema) =>
      schema.type === SchemaType.ENTITY &&
      (props.schema.fields?.some((field) => field.relatedEntityId === schema.id) ||
        rootEntityId === schema.id)
    )

  const availableFields = relevantEntities
    .flatMap((schema) => schema.fields?.map((field) => field.name) || [])

  if (availableFields.some((name) => isSingularPluralMatch(name, commandField))) {
    return false
  }

  return !relevantEntities.some((entity) => fieldNameImpliesEntity(commandField, entity.name))
}

const descriptionInvalid = ref(false)
const descriptionLength = ref(0)

const checkDescriptionLength = (event) => {
  descriptionLength.value = (event.target.innerText || '').length
  descriptionInvalid.value = descriptionLength.value > MAX_DESCRIPTION_LENGTH
}

const onBlurEntityDescription = async (event) => {
  const newDescription = (event.target.innerText || '').trim()
  const currentDescription = props.schema.description || ''

  if (newDescription.length > MAX_DESCRIPTION_LENGTH) {
    store.commit('snackbar/showMessage', {
      content: `Description must be at most ${MAX_DESCRIPTION_LENGTH} characters`,
      timeout: 4000,
      color: 'red'
    }, { root: true })
    event.target.innerText = currentDescription
    descriptionInvalid.value = false
    return
  }

  descriptionInvalid.value = false

  if (newDescription === currentDescription) {
    event.target.innerText = currentDescription
    return
  }

  const newSchemas = safeStructuredClone(workflow.value.schemas || [])
  const idx = newSchemas.findIndex((s) => s.id === props.schema.id)
  if (idx === -1) {
    return
  }
  newSchemas[idx].description = newDescription || null

  await workflowApi.updateSchemas({
    schemas: newSchemas,
    workflow: workflow.value
  })
}

const restoreEntityDescription = (event) => {
  event.target.innerText = props.schema.description || ''
  descriptionInvalid.value = false
  event.target.blur()
}

const handleChangeDataType = async (schemaId, fieldIndex, type, entityRef) => {
  switch (type) {
    case 'string':
    case 'number':
    case 'boolean':
      await setDataType(schemaId, fieldIndex, type, null)
      break
    case 'object':
      if (entityRef) {
        await setDataType(schemaId, fieldIndex, type, entityRef.id)
      }
      break
    case 'array':
      await setFieldCardinality(schemaId, fieldIndex, 'one-to-many')
      break
  }
}

onMounted(() => {
  initSortable()
})
</script>

<style>
.beam-table-container .v-table__wrapper {
  padding-top: 12px;
  padding-bottom: 2px;
}

.main-badge{
  font-size: 14px;
  padding: 5px 8px;
  border-radius: 4px;
  background: var(--light-2);
  color: var(--text-dark, #1F2833);
  max-width: fit-content;
}

.header-cell {
  font-size: 14px;
  font-weight: 500;
  padding: 6px 16px 6px 16px;
  background-color: var(--light-1);

  &.header-cell-verb {
    border-bottom: thin solid !important;
  }
}

.header-action {
  top: 50%;
  right: 10px;
  visibility: hidden;
  cursor: pointer;
  line-height: 33px;
  position: absolute;
  transform: translateY(-50%);
  color: var(--text-dark, #1F2833);
  display: flex;
  align-items: center;

  .v-icon {
    display: none;
  }
}

.field-name-wrapper {
  height: 100%;
  position: relative;
}

.field-name-wrapper:hover .header-action {
  visibility: visible;
}

.field-name-wrapper:hover .header-action .v-icon {
  display: block;
}

.header-action:focus .v-icon {
  visibility: visible;
}

.add-field-button {
  width: 50px;
  border-bottom: thin solid #EDEAE8 !important;
}

.add-field-button:hover {
  cursor: pointer;
  background-color: #A3C2E6;
}

tr > td:focus-within, td:focus {
  z-index: 3000;
  position: relative;
  outline: 2px solid #4D90FE;
}

.v-btn__content div {
  outline: none;
}
.beam-table-container table tbody td,
.beam-table-container table.v-table tbody th {
  height: 36px !important;
  min-width: 150px !important;
}

.table-container {
  border-bottom: 1px solid transparent !important
}

.field-label {
  width: 132px;
  font-size: 12px;
  background: var(--light-2);
  color: var(--text-light);
  padding: 6px 24px 6px 24px !important;
  position: relative;
}

tr:not(:last-child) .field-label {
  border-bottom: 1px solid #E5E3E1 !important;
}

.beam-table-container table tbody tr.description-row td {
  height: auto !important;
  vertical-align: top;
  padding: 10px 12px !important;
  font-style: italic;
  color: #5B5F66;
  font-size: 12px !important;
  line-height: 1.45;
}

.beam-table-container table tbody tr.description-row td.field-label {
  font-style: normal;
  padding: 10px 24px !important;
}

.order-last {
  order: 200;
}

.sortable-chosen {
  background: #eee;
}

.sortable-ghost {
  background: #E5E3E1;
}

.sortable-ghost > div {
  opacity: 0.01;
}

[data-theme="dark"] .sortable-chosen,
[data-theme="dark"] .sortable-ghost {
  background: var(--light-3);
}

.add-card-type-btn {
  min-width: 200px !important;
  border: 2px solid var(--command-border-color) !important;
}

.beam-required-field {
  &::after {
    display: inline-block;
    width: 12px;
  }

  &[contenteditable="true"]::after {
    content: '';
  }

  &:not([contenteditable="true"])::after {
    content: ' *';
    text-align: end;
  }
}

[data-theme="dark"] .beam-table-container table tbody tr.description-row td {
  color: var(--text-light);
}

[data-theme="dark"] .beam-table-container table td,
[data-theme="dark"] .beam-table-container table th {
  border-color: var(--light-3) !important;
}

[data-theme="dark"] .header-cell.header-cell-verb,
[data-theme="dark"] .add-field-button,
[data-theme="dark"] tr:not(:last-child) .field-label {
  border-bottom-color: var(--light-3) !important;
}
</style>

<style scoped>
.unused-entity-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.unused-entity-badge--orange {
  background-color: #FFF3E0;
  color: #E65100;
}

.unused-entity-badge--yellow {
  background-color: #FFFDE7;
  color: #F9A825;
}

.entity-type-chip {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 12px;
  letter-spacing: 0.3px;
  user-select: none;
  white-space: nowrap;
}

.entity-type-chip--entity {
  background-color: #FFF8E1;
  color: #F57F17;
  border: 1px solid #FFD54F;
}

.entity-type-chip--value-object {
  background-color: #FFF8E1;
  color: #F9A825;
  border: 1px solid #FFE082;
}

.entity-description {
  padding: 2px 24px 10px 24px;
  max-width: 90ch;
  font-size: 12px;
  font-style: italic;
  color: #6B7280;
  white-space: pre-wrap;
  outline: none;
  cursor: text;
}

.entity-description:focus {
  font-style: normal;
  color: #1F2833;
}

[data-theme="dark"] .entity-description:focus {
  color: var(--text-dark);
}

.entity-description--empty::before {
  content: attr(data-placeholder);
  color: #9CA3AF;
}

.entity-description--empty:focus::before {
  content: '';
}

.entity-description--invalid,
.entity-description--invalid:focus {
  color: #D32F2F;
}

.entity-description-error {
  padding: 0 24px 8px 24px;
  font-size: 11px;
  color: #D32F2F;
}

.sortableRow:hover .sortHandle {
  display: inline-flex !important;
}

.sortHandle {
  position: absolute !important;
  width: 36px !important;
  height: 24px !important;
  font-size: 28px !important;
  display: none !important;
  top: -12px;
  left: calc(50% - 18px);
  background-color: var(--light-1);
  border: 1px solid var(--light-4);
  border-radius: 4px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
}

.sortHandle:hover {
  cursor: grab;
}

.entity-sort-handle {
  cursor: grab;
  opacity: 0.7;
}

.entity-sort-handle:active {
  cursor: grabbing;
}

.row-action-menu {
  position: absolute;
  left: 4px;
  top: 50%;
  transform: translateY(-50%);
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s, visibility 0.2s;
  z-index: 10;
}

.example-data-row:hover .row-action-menu {
  visibility: visible;
  opacity: 1;
}

/* Make the 3-dot menu smaller */
.example-data-row .row-action-menu :deep(.v-btn) {
  width: 16px !important;
  height: 16px !important;
  min-width: 16px !important;
}

.example-data-row .row-action-menu :deep(.v-btn .v-icon) {
  width: 16px !important;
  height: 16px !important;
  font-size: 16px !important;
}
</style>
